import React, { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { IconProvider } from '@/components/ui';
import { DialogField, Dialog, DialogSection, getCachedDialogs } from '@/lib/stores/dialogStore';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/DefaultSelectDropdown';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';

// Common navigation data interface that all items share
export interface NavigationData {
  // New structure
  next_node?: string | null;
  next_dialog_node?: string | null;
  
  // Legacy structure (deprecated)
  next_field?: string | null;
  next_section?: string | null;
  next_dialog_section?: string | null;
  
  // Common
  next_dialog?: string | null;
}

type NavigationType = 'end' | 'to-node' | 'to-dialog';

interface DialogNavigationSelectorProps {
  // Current navigation values
  navigation: NavigationData;
  // Callback when navigation changes
  onNavigationChange: (updates: Partial<NavigationData>) => void;
  // Available nodes/sections in current dialog
  nodeIdentifiers?: string[];
  nodes?: { node_identifier: string; name?: string }[];
  // Legacy: available sections
  sectionIdentifiers?: string[];
  sections?: { section_identifier: string; name?: string }[];
  // Fields in current section (legacy)
  currentSectionFields?: DialogField[];
  // Current node/field/option ID to exclude from list
  currentItemId?: string;
  // Current node/section identifier to exclude from list
  currentNodeIdentifier?: string;
  currentSectionIdentifier?: string;
  // Current dialog ID to exclude from dialog list
  currentDialogId?: string;
  // Game ID for fetching dialogs
  gameId?: string;
  // Whether to show "To field in this section" option (legacy, deprecated)
  showFieldOption?: boolean;
  // Label text (e.g., "After this:", "Action", "Then")
  label?: string;
  // Size variant
  size?: 'sm' | 'default';
}

export const DialogNavigationSelector = React.memo(function DialogNavigationSelector({
  navigation,
  onNavigationChange,
  nodeIdentifiers,
  nodes,
  sectionIdentifiers,
  sections,
  currentSectionFields,
  currentItemId,
  currentNodeIdentifier,
  currentSectionIdentifier,
  currentDialogId,
  gameId,
  showFieldOption = true,
  label = 'After this:',
  size = 'default',
}: DialogNavigationSelectorProps) {
  const { selectedGame } = useSimplifiedUserData();
  const effectiveGameId = gameId || selectedGame?.id;

  // Support both new (nodes) and legacy (sections) structures
  const effectiveIdentifiers = nodeIdentifiers || sectionIdentifiers || [];
  const effectiveItems = nodes || sections || [];
  const effectiveCurrentIdentifier = currentNodeIdentifier || currentSectionIdentifier;

  // Get available dialogs (exclude current dialog)
  const availableDialogs = useMemo(() => {
    if (!effectiveGameId) return [];
    const allDialogs = getCachedDialogs(effectiveGameId);
    return allDialogs.filter(d => d.id !== currentDialogId);
  }, [effectiveGameId, currentDialogId]);

  // Filter available nodes/sections (exclude current node/section)
  const availableItems = useMemo(() => {
    if (!effectiveCurrentIdentifier) return effectiveIdentifiers;
    return effectiveIdentifiers.filter(id => id !== effectiveCurrentIdentifier);
  }, [effectiveIdentifiers, effectiveCurrentIdentifier]);

  // Get nodes/sections from selected external dialog
  const selectedExternalDialog = useMemo(() => {
    if (!navigation.next_dialog) return null;
    return availableDialogs.find(d => d.id === navigation.next_dialog) || null;
  }, [navigation.next_dialog, availableDialogs]);

  const externalDialogItems = useMemo(() => {
    if (!selectedExternalDialog?.data) return [];
    // Try new structure first, fall back to legacy
    if (selectedExternalDialog.data.nodes) {
      return selectedExternalDialog.data.nodes.map(n => ({
        identifier: n.node_identifier,
        name: n.name || n.node_identifier,
      }));
    }
    if (selectedExternalDialog.data.sections) {
      return selectedExternalDialog.data.sections.map(s => ({
        identifier: s.section_identifier,
        name: s.name || s.section_identifier,
      }));
    }
    return [];
  }, [selectedExternalDialog]);

  // Determine current navigation type
  const navigationType: NavigationType = useMemo(() => {
    if (navigation.next_dialog !== null && navigation.next_dialog !== undefined) {
      return 'to-dialog';
    }
    // Check new structure first
    if (navigation.next_node !== null && navigation.next_node !== undefined) {
      return 'to-node';
    }
    // Fall back to legacy structure
    if (navigation.next_section !== null && navigation.next_section !== undefined) {
      return 'to-node'; // Map to new type
    }
    if (showFieldOption && navigation.next_field !== null && navigation.next_field !== undefined) {
      return 'to-node'; // Map to new type
    }
    return 'end';
  }, [navigation, showFieldOption]);

  // Handle navigation type change
  const handleTypeChange = (value: string) => {
    const type = value as NavigationType;
    
    if (type === 'end') {
      onNavigationChange({
        next_node: null,
        next_dialog: null,
        next_dialog_node: null,
        // Legacy
        next_section: null,
        next_field: null,
        next_dialog_section: null,
      });
    } else if (type === 'to-node') {
      onNavigationChange({
        next_dialog: null,
        next_dialog_node: null,
        next_node: '', // Will be selected in dropdown
        // Legacy - use empty string instead of null to preserve selection
        next_section: '', // Keep empty string so it doesn't revert to "end" on re-render
        next_field: null,
        next_dialog_section: null,
      });
    } else if (type === 'to-dialog') {
      onNavigationChange({
        next_node: null,
        next_dialog: '', // Will be selected in dropdown
        next_dialog_node: null,
        // Legacy
        next_section: null,
        next_field: null,
        next_dialog_section: null,
      });
    }
  };

  const selectHeight = size === 'sm' ? 'h-7' : 'h-8';
  const textSize = size === 'sm' ? 'text-xs' : 'text-sm';

  return (
    <div className="space-y-2">
      {label && (
        <Label className={`${textSize} font-semibold`}>{label}</Label>
      )}
      
      {/* Navigation Type Selection */}
      <Select value={navigationType} onValueChange={handleTypeChange}>
        <SelectTrigger className={`${selectHeight} ${textSize}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="end">End</SelectItem>
          <SelectItem value="to-node">To node</SelectItem>
          <SelectItem value="to-dialog">To dialog (external)</SelectItem>
        </SelectContent>
      </Select>

      {/* Node Selection (if to-node) */}
      {navigationType === 'to-node' && (
        <Select
          value={navigation.next_node || navigation.next_section || navigation.next_field || ''}
          onValueChange={(value) => onNavigationChange({ 
            next_node: value || null,
            next_section: value || null, // For backward compatibility
          })}
        >
          <SelectTrigger className={`${selectHeight} ${textSize}`}>
            <SelectValue placeholder="Select node...">
              {(() => {
                const currentValue = navigation.next_node || navigation.next_section || navigation.next_field;
                if (!currentValue) return 'Select node...';
                
                const item = effectiveItems.find((i: any) => 
                  (i.node_identifier === currentValue) || (i.section_identifier === currentValue)
                );
                return item?.name || currentValue;
              })()}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {availableItems.length === 0 ? (
              <SelectItem value="__empty" disabled>
                No other nodes available
              </SelectItem>
            ) : (
              availableItems.map((identifier) => {
                const item = effectiveItems.find((i: any) => 
                  (i.node_identifier === identifier) || (i.section_identifier === identifier)
                );
                return (
                  <SelectItem key={identifier} value={identifier}>
                    {item?.name || identifier}
                  </SelectItem>
                );
              })
            )}
          </SelectContent>
        </Select>
      )}

      {/* External Dialog Selection */}
      {navigationType === 'to-dialog' && (
        <div className="space-y-2">
          {/* Dialog Selection */}
          <Select
            value={navigation.next_dialog || ''}
            onValueChange={(value) => 
              onNavigationChange({
                next_dialog: value || null,
                next_dialog_node: null, // Reset node when dialog changes
                next_dialog_section: null, // Legacy
              })
            }
          >
            <SelectTrigger className={`${selectHeight} ${textSize}`}>
              <SelectValue placeholder="Select dialog...">
                {navigation.next_dialog && availableDialogs.length > 0 ? (() => {
                  const targetDialog = availableDialogs.find(d => d.id === navigation.next_dialog);
                  return targetDialog?.name || 'Select dialog...';
                })() : 'Select dialog...'}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {availableDialogs.length === 0 ? (
                <SelectItem value="__empty" disabled>
                  No other dialogs available
                </SelectItem>
              ) : (
                availableDialogs.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>

          {/* Node Selection (within selected dialog) */}
          {navigation.next_dialog && externalDialogItems.length > 0 && (
            <Select
              value={navigation.next_dialog_node || navigation.next_dialog_section || ''}
              onValueChange={(value) => onNavigationChange({ 
                next_dialog_node: value || null,
                next_dialog_section: value || null, // Legacy
              })}
            >
              <SelectTrigger className={`${selectHeight} ${textSize}`}>
                <SelectValue placeholder="Select node (optional)...">
                  {(() => {
                    const currentValue = navigation.next_dialog_node || navigation.next_dialog_section;
                    if (!currentValue) return 'Select node (optional)...';
                    const targetItem = externalDialogItems.find(i => i.identifier === currentValue);
                    return targetItem?.name || currentValue;
                  })()}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Start from beginning</SelectItem>
                {externalDialogItems.map((item) => (
                  <SelectItem key={item.identifier} value={item.identifier}>
                    {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Info display when dialog is selected */}
          {selectedExternalDialog && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
              <IconProvider iconstring="fa-arrow-up-right-from-square" size={10} />
              <span>→ {selectedExternalDialog.name}</span>
              {(navigation.next_dialog_node || navigation.next_dialog_section) && (
                <span className="text-primary">• {navigation.next_dialog_node || navigation.next_dialog_section}</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
});
